import React, { useState } from "react";
import { Box, Typography, Button, Container, Grid, Paper } from "@mui/material";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useSession } from "../hooks/useSession";
import AuthModal from "../components/AuthModal";
import SEO from '../components/SEO';
import LazyImage from "../components/LazyImage"; // Add this import at the top

const MainPage = () => {
  const { session } = useSession();
  const [authModalOpen, setAuthModalOpen] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const features = [
    {
      title: "Versus Live Mode",
      description: "Join real-time battles where the community decides which character is stronger or more popular.",
      icon: "https://wwwikvfwquvafkoopemw.supabase.co/storage/v1/object/public/manga_versus/images/icons/kunai.png",
      link: "/versus-live",
    },
    {
      title: "Solo Versus Mode",
      description: "Battle characters one-on-one at your own pace. Choose your scope and vote on endless matchups.",
      icon: "https://wwwikvfwquvafkoopemw.supabase.co/storage/v1/object/public/manga_versus/images/icons/katana.png",
      link: "/character-voting",
    },
    {
      title: "Character Rankings",
      description: "Explore comprehensive leaderboards showing power levels and popularity across all manga series.",
      icon: "https://wwwikvfwquvafkoopemw.supabase.co/storage/v1/object/public/manga_versus/images/icons/torii-icon.png",
      link: "/leaderboard",
    },
    {
      title: "Multiple Categories",
      description: "Vote on power levels, popularity, and discover favorites across different character types.",
      icon: "https://wwwikvfwquvafkoopemw.supabase.co/storage/v1/object/public/manga_versus/images/icons/kakashi-anbu-mask-icon.png",
      link: "/character-list", // Added link to characters page
    },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Manga Versus",
    "description": "Anime powerscaling and character voting platform",
    "url": "https://manga-versus.com",
    "category": "Entertainment",
    "applicationCategory": "GameApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  return (
    <>
      <SEO 
        title="Anime Powerscaling & Character Voting Platform"
        description="Vote on epic anime character battles and power scaling debates. Join thousands in ranking manga characters by power levels and popularity. Real-time community voting!"
        keywords="anime powerscaling, manga character voting, anime vs battles, character tier list, anime character rankings, power scaling debate"
        structuredData={structuredData}
      />
      <Box sx={{ minHeight: "100vh" }}>
        {/* Spacer for navbar */}
        <Box sx={{ height: { xs: 72, sm: 80 } }} />

        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 12 } }}>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Hero Section */}
            <Box sx={{ textAlign: "center", mb: { xs: 10, md: 16 } }}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: 48, sm: 64, md: 80 },
                    fontWeight: 900,
                    background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    textAlign: "center",
                    mb: 4,
                    letterSpacing: 2,
                  }}
                >
                  MANGA VERSUS
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#e0e0e0",
                    fontWeight: 600,
                    mb: 6,
                    fontSize: { xs: 20, sm: 24, md: 32 },
                    maxWidth: 800,
                    mx: "auto",
                    lineHeight: 1.4,
                  }}
                >
                  The Ultimate Battleground for Manga Characters
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#bbb",
                    mb: 8,
                    fontSize: { xs: 16, sm: 18 },
                    maxWidth: 600,
                    mx: "auto",
                    lineHeight: 1.6,
                  }}
                >
                  Join other manga fans in epic voting battles. Compare power levels, popularity, and determine once and for all who reigns supreme in the manga universe.
                </Typography>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div variants={itemVariants}>
                <Box sx={{ display: "flex", gap: 4, justifyContent: "center", flexWrap: "wrap" }}>
                  <Button
                    component={Link}
                    to="/versus-live"
                    variant="contained"
                    sx={{
                      background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                      color: "#181818",
                      fontWeight: 700,
                      fontSize: { xs: 16, sm: 20 },
                      px: { xs: 3, sm: 6 },
                      py: { xs: 1.5, sm: 2 },
                      borderRadius: 3,
                      boxShadow: "0 4px 20px rgba(244, 147, 48, 0.4)",
                      "&:hover": {
                        background: "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)",
                        boxShadow: "0 6px 25px rgba(244, 147, 48, 0.6)",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    JOIN LIVE BATTLE
                  </Button>

                  <Button
                    component={Link}
                    to="/leaderboard"
                    variant="outlined"
                    sx={{
                      borderColor: "#f49330",
                      color: "#f49330",
                      fontWeight: 700,
                      fontSize: { xs: 16, sm: 20 },
                      px: { xs: 3, sm: 6 },
                      py: { xs: 1.5, sm: 2 },
                      borderRadius: 3,
                      borderWidth: 2,
                      "&:hover": {
                        background: "rgba(244, 147, 48, 0.1)",
                        borderColor: "#fff176",
                        color: "#fff176",
                        borderWidth: 2,
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    VIEW RANKINGS
                  </Button>

                  {/* New Solo Battle Button */}
                  <Button
                    component={Link}
                    to="/character-voting"
                    variant="contained"
                    sx={{
                      background: "linear-gradient(45deg, #6366f1 30%, #8b5cf6 90%)",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: { xs: 16, sm: 20 },
                      px: { xs: 3, sm: 6 },
                      py: { xs: 1.5, sm: 2 },
                      borderRadius: 3,
                      boxShadow: "0 4px 20px rgba(99, 102, 241, 0.4)",
                      "&:hover": {
                        background: "linear-gradient(45deg, #4f46e5 30%, #7c3aed 90%)",
                        boxShadow: "0 6px 25px rgba(99, 102, 241, 0.6)",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    JOIN SOLO BATTLE
                  </Button>
                </Box>
              </motion.div>
            </Box>

            {/* Features Section */}
            <motion.div variants={itemVariants}>
              <Typography
                variant="h3"
                sx={{
                  textAlign: "center",
                  color: "#fff",
                  fontWeight: 800,
                  mb: 8,
                  fontSize: { xs: 28, sm: 36 },
                }}
              >
                How It Works
              </Typography>
            </motion.div>

            <Grid container spacing={6} sx={{ mb: { xs: 12, md: 16 }, justifyContent: "center" }}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={6} lg={5} key={index} sx={{ display: "flex", justifyContent: "center" }}>
                  <motion.div variants={itemVariants} style={{ width: "100%", maxWidth: 500 }}>
                    <Paper
                      component={feature.link ? Link : "div"}
                      to={feature.link}
                      sx={{
                        background: "rgba(35, 35, 35, 0.85)", // Changed from solid to transparent
                        border: "2px solid #3e3e3e",
                        borderRadius: 4,
                        p: 5,
                        height: 200, // Fixed height to ensure all boxes are same size
                        display: "flex",
                        alignItems: "flex-start",
                        width: "100%",
                        maxWidth: 500, // Max width to prevent boxes from getting too wide
                        textDecoration: "none",
                        color: "inherit",
                        cursor: feature.link ? "pointer" : "default",
                        backdropFilter: "blur(10px)", // Add glass effect
                        WebkitBackdropFilter: "blur(10px)", // Add glass effect for Safari
                        transition: "all 0.3s ease",
                        "&:hover": {
                          borderColor: feature.link ? "#f49330" : "#3e3e3e",
                          boxShadow: feature.link ? "0 8px 32px rgba(244, 147, 48, 0.2)" : "none",
                          transform: feature.link ? "translateY(-4px)" : "none",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3, width: "100%" }}>
                        <LazyImage
                          src={feature.icon}
                          alt={feature.title}
                          threshold={0.2}
                          sx={{
                            width: 48,
                            height: 48,
                            flexShrink: 0,
                            filter: "brightness(0) saturate(100%) invert(100%)",
                            objectFit: "cover",
                            objectPosition: "center",
                            ...(feature.title === "Multiple Categories" && {
                              transform: "scale(1.5)",
                            }),
                            ...(feature.title === "Character Rankings" && {
                              transform: "scale(1.1)",
                            })
                          }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h5"
                            sx={{
                              color: "#fff",
                              fontWeight: 700,
                              mb: 3,
                              fontSize: { xs: 18, sm: 20 },
                            }}
                          >
                            {feature.title}
                          </Typography>
                          <Typography
                            sx={{
                              color: "#bbb",
                              fontSize: { xs: 14, sm: 16 },
                              lineHeight: 1.6,
                            }}
                          >
                            {feature.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            {/* Stats Section */}
            <motion.div variants={itemVariants}>
              <Box
                sx={{
                  background: "linear-gradient(135deg, rgba(244, 147, 48, 0.85) 0%, rgba(255, 241, 118, 0.85) 100%)",
                  backdropFilter: "blur(10px)", // Add glass effect
                  WebkitBackdropFilter: "blur(10px)", // Add glass effect for Safari
                  borderRadius: 4,
                  p: { xs: 6, md: 8 },
                  textAlign: "center",
                  mb: { xs: 12, md: 16 },
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: "#181818",
                    fontWeight: 800,
                    mb: 4,
                    fontSize: { xs: 24, sm: 32 },
                  }}
                >
                  Join the Ultimate Manga Community
                </Typography>
                <Typography
                  sx={{
                    color: "#333",
                    fontSize: { xs: 16, sm: 18 },
                    maxWidth: 600,
                    mx: "auto",
                    lineHeight: 1.6,
                  }}
                >
                  Thousands of battles fought • Hundreds of characters ranked • One definitive leaderboard
                </Typography>
              </Box>
            </motion.div>

            {/* Final CTA */}
            <motion.div variants={itemVariants}>
              <Box sx={{ textAlign: "center", pb: 8 }}>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    mb: 6,
                    fontSize: { xs: 24, sm: 32 },
                  }}
                >
                  Ready to Settle the Debate?
                </Typography>
                <Button
                  component={session ? Link : "button"}
                  to={session ? "/versus-live" : undefined}
                  onClick={session ? undefined : () => setAuthModalOpen(true)}
                  variant="contained"
                  sx={{
                    background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                    color: "#181818",
                    fontWeight: 700,
                    fontSize: 18,
                    px: 8,
                    py: 2.5,
                    borderRadius: 3,
                    boxShadow: "0 4px 20px rgba(244, 147, 48, 0.4)",
                    "&:hover": {
                      background: "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)",
                      boxShadow: "0 6px 25px rgba(244, 147, 48, 0.6)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  {session ? "START VOTING NOW" : "JOIN NOW"}
                </Button>
              </Box>
            </motion.div>
          </motion.div>
        </Container>
        
        <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} />
      </Box>
    </>
  );
};

export default MainPage;